import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Paper,
  Chip,
  Card,
  CircularProgress,
} from "@mui/material";
import {
  Close,
  CloudUpload,
  ArrowBack,
  ArrowForward,
} from "@mui/icons-material";
import ImportPreview from "./ImportPreview";
import ImportResults from "./ImportResults";

/**
 * Reusable ImportDialog component with multi-step workflow
 * 
 * @param {boolean} open - Dialog open state
 * @param {Function} onClose - Close handler
 * @param {Function} onImport - Import handler: (file, data) => Promise<results>
 * @param {string} title - Dialog title
 * @param {string} templateUrl - Template file download URL
 * @param {Array} previewColumns - Column definitions for preview table
 * @param {Function} validateRow - Row validation function: (row) => { valid: boolean, error?: string }
 * @param {string} accept - Accepted file types (default: ".xlsx,.xls,.csv")
 * @param {number} maxSize - Max file size in MB (default: 5)
 */
const ImportDialog = ({
  open,
  onClose,
  onImport,
  title = "Import Data",
  templateUrl,
  previewColumns = [],
  validateRow,
  accept = ".xlsx,.xls,.csv",
  maxSize = 5,
}) => {
  const [step, setStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");

  const steps = ["Select File", "Preview & Validate", "Import"];

  const handleFileSelect = (event) => {
    if (importing) {
      event.target.value = "";
      return;
    }
    const file = event.target.files[0];
    if (!file) return;

    setError("");

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File size exceeds maximum of ${maxSize}MB`);
      return;
    }

    setSelectedFile(file);

    // Simulate file parsing (in real app, use xlsx library)
    // For now, we'll expect the parent to handle file parsing
    // This is a placeholder - actual implementation would parse the file
    setPreviewData([]);
    setStep(1);
  };

  const handleImport = async () => {
    if (!selectedFile || previewData.length === 0) return;

    setImporting(true);
    setError("");

    try {
      const results = await onImport(selectedFile, previewData);
      setImportResults(results);
      setStep(2);
    } catch (err) {
      setError(err.message || "Import failed. Please try again.");
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setStep(0);
    setSelectedFile(null);
    setPreviewData([]);
    setImportResults(null);
    setError("");
    onClose();
  };

  const getRowStatus = (row) => {
    if (!validateRow) return "valid";
    const validation = validateRow(row);
    return validation.valid ? "valid" : "error";
  };

  const getRowError = (row) => {
    if (!validateRow) return null;
    const validation = validateRow(row);
    return validation.valid ? null : validation.error;
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleClose();
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          py: 2.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <CloudUpload sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {title}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Bulk upload data from Excel or CSV
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: "#f8f9fa",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stepper activeStep={step} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      "&.Mui-active": { color: "#667eea" },
                      "&.Mui-completed": { color: "#764ba2" },
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={step === steps.indexOf(label) ? 600 : 400}
                  >
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ p: 3, bgcolor: "#fff", overflow: "auto", maxHeight: "60vh" }}>
          {step === 0 && (
            <Box>
              <Paper
                elevation={0}
                sx={{
                  border: "2px dashed",
                  borderColor: importing ? "#cbd5e1" : "#b8c4ce",
                  borderRadius: 3,
                  p: 5,
                  textAlign: "center",
                  cursor: importing ? "not-allowed" : "pointer",
                  opacity: importing ? 0.65 : 1,
                  pointerEvents: importing ? "none" : "auto",
                  background:
                    "linear-gradient(180deg, rgba(102,126,234,0.03) 0%, rgba(118,75,162,0.03) 100%)",
                  transition: "all 0.3s ease",
                  "&:hover": importing
                    ? {}
                    : {
                        borderColor: "#667eea",
                        background:
                          "linear-gradient(180deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.08) 100%)",
                      },
                }}
                component={importing ? "div" : "label"}
              >
                <input
                  type="file"
                  hidden
                  accept={accept}
                  onChange={handleFileSelect}
                  disabled={importing}
                />
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                    boxShadow: "0 4px 20px rgba(102,126,234,0.3)",
                  }}
                >
                  <CloudUpload sx={{ fontSize: 40, color: "white" }} />
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {importing ? "Import in progress..." : "Drop your file here or click to browse"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {importing
                    ? "File selection is disabled until the import completes."
                    : `Supports ${accept} files (Max ${maxSize}MB)`}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5 }}>
                  {accept.split(",").map((ext) => (
                    <Chip
                      key={ext}
                      label={ext.trim()}
                      size="small"
                      sx={{
                        bgcolor: "rgba(102,126,234,0.1)",
                        color: "#667eea",
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Box>
              </Paper>

              {templateUrl && (
                <Card variant="outlined" sx={{ mt: 3, borderRadius: 2 }}>
                  <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Need a template?
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Download our Excel template with sample data
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      href={templateUrl}
                      download
                    >
                      Download Template
                    </Button>
                  </Box>
                </Card>
              )}

              {error && (
                <Box sx={{ mt: 2, p: 2, bgcolor: "error.light", borderRadius: 2 }}>
                  <Typography variant="body2" color="error">
                    {error}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {step === 1 && (
            <Box>
              {previewData.length > 0 ? (
                <ImportPreview
                  data={previewData}
                  columns={previewColumns}
                  getRowStatus={getRowStatus}
                  getRowError={getRowError}
                />
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No preview data available. Please ensure the file was parsed correctly.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {step === 2 && (
            <ImportResults
              results={importResults}
              onDownloadErrors={() => {
                // Handle download errors
              }}
              onRetry={() => {
                setStep(1);
                setImportResults(null);
              }}
              onClose={handleClose}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, borderTop: "1px solid", borderColor: "divider" }}>
        {step === 0 && (
          <Button onClick={handleClose} disabled={importing} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
        )}
        {step === 1 && (
          <>
            <Button
              onClick={() => setStep(0)}
              startIcon={<ArrowBack />}
              disabled={importing}
              sx={{ borderRadius: 2 }}
            >
              Back
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={previewData.length === 0 || importing}
              startIcon={importing ? <CircularProgress size={16} /> : <ArrowForward />}
              sx={{ borderRadius: 2 }}
            >
              {importing ? "Importing..." : "Import"}
            </Button>
          </>
        )}
        {step === 2 && (
          <Button variant="contained" onClick={handleClose} sx={{ borderRadius: 2 }}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;

